import { useRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { useScrollEdges } from './useScrollEdges.ts';

interface TableScrollBoxProps {
  /** A table that keeps a minimum width is one expected to scroll sideways. */
  scrolls: boolean;
  className?: string;
  children: ReactNode;
}

/** The box a wide table scrolls in, shaded on each side that still hides columns. */
export function TableScrollBox({ scrolls, className, children }: TableScrollBoxProps) {
  const scrollBox = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(scrollBox);

  return (
    <div className='relative'>
      {/*
       * A region that scrolls has to be reachable without a mouse. Only a table
       * expected to scroll takes the tab stop: on one that fits its box it
       * would be a stop on the way to nothing.
       */}
      <div
        ref={scrollBox}
        tabIndex={scrolls ? 0 : undefined}
        className={cn(
          'overflow-auto rounded-md',
          scrolls &&
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
          className,
        )}
      >
        {children}
      </div>

      {/*
       * A shade on each side that still hides columns: on a phone a cut-off
       * table otherwise looks like all there is.
       */}
      {edges.start && (
        <span
          data-slot='data-table-edge'
          data-side='start'
          aria-hidden='true'
          className='pointer-events-none absolute inset-y-0 left-0 z-2 w-6 rounded-l-md bg-linear-to-r from-ink/12 to-transparent'
        />
      )}
      {edges.end && (
        <span
          data-slot='data-table-edge'
          data-side='end'
          aria-hidden='true'
          className='pointer-events-none absolute inset-y-0 right-0 z-2 w-6 rounded-r-md bg-linear-to-l from-ink/12 to-transparent'
        />
      )}
    </div>
  );
}
